/*
 * Copyright 2018 - 2021 Swiss Federal Institute of Technology Lausanne (EPFL)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0.
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * This open source software code was developed in part or in whole in the
 * Human Brain Project, funded from the European Union's Horizon 2020
 * Framework Programme for Research and Innovation under
 * Specific Grant Agreements No. 720270, No. 785907, and No. 945539
 * (Human Brain Project SGA1, SGA2 and SGA3).
 *
 */

import React, { useState } from "react";
import { BrowserRouter, useSearchParams, Route, Routes, useLocation, matchPath } from "react-router-dom";
import { validate as uuidValidate } from "uuid";

import Button from "react-bootstrap/Button";

import API from "./services/API";
import AuthAdapter from "./services/AuthAdapter";
import KeycloakAuthAdapter from "./services/KeycloakAuthAdapter";
import APIProvider from "./components/APIProvider";
import AuthProvider from "./components/AuthProvider";
import Error from "./components/Error";
import ErrorBoundary from "./components/ErrorBoundary";
import Authenticate from "./components/Authenticate";
import Settings from "./components/Settings";
import Initialization from "./components/Initialization";
import ContactText from "./components/ContactText";

import types from "./services/Types";

import "./App.scss";

const COLLAB_SPACE_PREFIX = "collab-";
const COLLAB_ID = "clb-collab-id";

const UUID_PREFIXES = ["d-", "swc-"];

const getUUIDWithPrefix = (collab_id:string|null|undefined, prefix: string):string|undefined  => {
  if (collab_id && collab_id.startsWith(prefix)) {
    const uuid = collab_id.substring(prefix.length);
    if (uuidValidate(uuid)) {
      return uuid;
    }
  }
  return undefined;
};

const getUUID = (collab_id:string|null|undefined):string|undefined  => {
  let uuid = undefined;
  let idx = 0;
  while (!uuid && idx < UUID_PREFIXES.length) {
    uuid = getUUIDWithPrefix(collab_id, UUID_PREFIXES[idx]);
    idx++;
  }
  return uuid;
};

const App = ({ api, authAdapter }: { api: API, authAdapter?: AuthAdapter}) => {
  
  const location = useLocation();

  const [globalError, setGlobalError] = useState<string|undefined>(undefined);
  
  const [searchParams] = useSearchParams();
  const collab_id = searchParams.get(COLLAB_ID);
  const space = collab_id?`${COLLAB_SPACE_PREFIX}${collab_id}`:undefined;
  const instanceId = getUUID(collab_id);

  const handleGlobalError = (error:Error) => setGlobalError(error.message);

  const handleDismissGlobalError = () => {
    setGlobalError(undefined);
    document.location.reload();
  };

  const matchType = matchPath({ path: `/types/:type` }, location.pathname);
  const type = matchType?.params?.type;
  if(!type || !types[type]) {
    return (
      <div className="container" >
        <div className="body">
          <Error>
            <h3>Failed to launch KG Editor Community App.</h3>
            <i>The path &quot;{location.pathname}&quot; is not supported yet.</i><br /><br />
            <ContactText />
          </Error>
        </div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="container" >
        <div className="body">
          <Error>
            <h3>Failed to launch KG Editor Community App</h3>
            <i>The mandatory parameter &quot;{COLLAB_ID}&quot; is mandatory but is missing!</i><br /><br />
            <ContactText />
          </Error>
        </div>
      </div>
    );
  }

  return (
    <div className="container" >
      <div className="body">
        <ErrorBoundary onError={handleGlobalError}>
          {globalError ?
            <Error>
              <h3>An unexpected error has occured.</h3><br />
              <i>{globalError}</i><br />
              <Button variant="primary" onClick={handleDismissGlobalError}>Retry</Button>
            </Error>
            :
            <APIProvider api={api}>
              <AuthProvider adapter={authAdapter} >
                <Settings>
                  {settings => {
                      if (authAdapter instanceof KeycloakAuthAdapter) {
                        if (settings?.keycloak) {
                          authAdapter.setConfig(settings?.keycloak);
                        } else {
                          return (
                            <Error>
                              <h3>Failed to initilize KG Editor Community App.</h3>
                              <i>Service settings endpoint is missing keycloak configuration.</i><br /><br />
                              <ContactText />
                            </Error>
                          );
                        }
                      }
                      return (
                        <Authenticate>
                          <Routes>
                            {Object.entries(types).map(([type, schemas]) => (
                              <Route key={type} path={`/types/${type}`} element={ <Initialization space={space} schemas={schemas} instanceId={instanceId} /> } />
                            ))}
                          </Routes>
                        </Authenticate>
                      );
                  }}
                </Settings>
              </AuthProvider>
            </APIProvider>
          }
        </ErrorBoundary>
      </div>
    </div>
  );
}

const Component = ({ basename, api, authAdapter }: { basename?: string, api: API, authAdapter?: AuthAdapter}) => (
  <BrowserRouter basename={basename} >
    <App api={api} authAdapter={authAdapter} />
  </BrowserRouter>
);

export default Component;